import {
  IParentTicketItem,
  ISimpleEventDetail,
  ITicketItem,
  ITicketsResponseData,
  Phase,
  SupportedLanguage,
} from "interfaces";
import { get, isEmpty } from "lodash";
import { Fragment, useState } from "react";
import { CollapsibleSection } from "components/collapsibleSection";
import { SubTickets } from "./subTickets";
import { HistoryLodModal } from "components/historyLogModal";
import { ParentTicket } from "./parentTicket";
import { DropdownMenu } from "components/dropdownMenu";
import { DotsVerticalIcon } from "@heroicons/react/outline";
import { useTranslation } from "@refinedev/core";
import { endpointService } from "components/shared/services/endpointService";

interface ParentTicketModalConfig {
  open: boolean;
  ticketData: IParentTicketItem | null;
}

const INITIAL_PARENT_TICKET_MODAL_CONFIG: ParentTicketModalConfig = {
  open: false,
  ticketData: null,
};

export const Tickets = ({
  eventDetailData,
  ticketPhases,
  tickets,
}: {
  eventDetailData: ISimpleEventDetail;
  ticketPhases: Phase[];
  tickets: ITicketsResponseData["tickets"];
}) => {
  const { translate } = useTranslation();
  const [openLogModalId, setOpenLogModalId] = useState<string | null>(null);
  const [parentTicketModalConfig, setParentTicketModalConfig] = useState(
    INITIAL_PARENT_TICKET_MODAL_CONFIG
  );
  const [openFieldId, setOpenFielIdId] = useState<string | null>(null);

  const _onCloseParentTicketModal = () => {
    setParentTicketModalConfig(INITIAL_PARENT_TICKET_MODAL_CONFIG);
  };

  const _handleOpenField = (id: string) => {
    setOpenFielIdId((prevId) => (prevId === id ? null : id));
  };

  return (
    <div className="box-container">
      <div>
        <div className="grid sm:grid-cols-6 lg:gap-x-6">
          <div className="col-span-full flex flex-col gap-y-4">
            {tickets.map((field: ITicketItem, index: number) => (
              <Fragment key={index}>
                {!isEmpty(field.ticket) ? (
                  <div
                    className={`border rounded-lg border-gray-300`}
                    key={field.ticket.id}
                  >
                    <CollapsibleSection
                      label={`${get(
                        field.ticket,
                        `category.${
                          eventDetailData.languages.includes(
                            SupportedLanguage.EN
                          )
                            ? "en"
                            : eventDetailData.languages[0] ?? "vi"
                        }`
                      )}`}
                      customOpen={{
                        open: openFieldId === field.ticket.id,
                        onClick: () =>
                          _handleOpenField(field?.ticket?.id ?? ""),
                      }}
                      isAnimated={false}
                      toolbar={
                        <DropdownMenu
                          label={
                            <DotsVerticalIcon className="size-4 text-brand-500" />
                          }
                          items={[
                            {
                              label: translate("view"),
                              onClick: () =>
                                setParentTicketModalConfig({
                                  open: true,
                                  ticketData: field.ticket,
                                }),
                            },
                            {
                              label: "Log",
                              onClick: () =>
                                setOpenLogModalId(field.ticket?.id ?? ""),
                            },
                          ]}
                        />
                      }
                    >
                      <div className="px-4 py-4 sm:px-6">
                        <p className="mb-4">{translate("eventSetup.phaseAndTicket.sellInPhase")}</p>
                        <SubTickets
                          eventDetailData={eventDetailData}
                          ticketData={field ?? null}
                          ticketPhases={ticketPhases ?? []}
                          setOpenLogModalId={setOpenLogModalId}
                        />
                      </div>
                    </CollapsibleSection>
                  </div>
                ) : (
                  <SubTickets
                    eventDetailData={eventDetailData}
                    ticketData={field ?? null}
                    ticketPhases={ticketPhases ?? []}
                    key={index}
                    phaseTicket={false}
                    setOpenLogModalId={setOpenLogModalId}
                  />
                )}
              </Fragment>
            ))}
          </div>
        </div>
        {isEmpty(tickets) ? (
          <div className="text-center text-sm">
            <p className="text-center text-sm">{translate("eventSetup.phaseAndTicket.noTickets")}</p>
          </div>
        ) : (
          <></>
        )}
      </div>
      <ParentTicket
        eventDetailData={eventDetailData}
        open={parentTicketModalConfig.open}
        onClose={_onCloseParentTicketModal}
        ticketData={parentTicketModalConfig.ticketData}
        title={translate("eventSetup.phaseAndTicket.parentTicket")}
      />
      <HistoryLodModal
        resource={endpointService.getEventEndpoint("getTicketHistoryLog", {
          logId: openLogModalId ?? "",
        })}
        open={openLogModalId ? true : false}
        onClose={() => setOpenLogModalId(null)}
        key="ticket"
        id="ticket"
        customFilters={
          (tickets ?? []).find((ticket) => ticket.ticket?.id === openLogModalId)
            ? [{ field: "parent_ticket", operator: "eq", value: true }]
            : []
        }
      />
    </div>
  );
};
